import { Link, useParams, Navigate } from "react-router-dom";
import { Calendar, Clock, ArrowRight, ArrowLeft, Phone, AlertCircle } from "lucide-react";
import { BLOG_POSTS, getPostMeta, getRelatedPosts } from "@/lib/blog";
import { BRAND } from "@/lib/site";
import SEO from "@/components/SEO";
import CallStrip from "@/components/CallStrip";
import QuoteForm from "@/components/QuoteForm";

const SITE_URL =
  (typeof window !== "undefined" && window.location.origin) ||
  "https://flowgridplumbing.com.au";

function formatDate(iso) {
  return new Date(iso).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function Block({ block }) {
  switch (block.type) {
    case "h2":
      return (
        <h2 className="mt-12 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
          {block.content}
        </h2>
      );
    case "h3":
      return (
        <h3 className="mt-8 font-display text-lg font-bold text-slate-900 sm:text-xl">
          {block.content}
        </h3>
      );
    case "ul":
      return (
        <ul className="mt-5 space-y-2.5 pl-1">
          {block.content.map((item) => (
            <li key={item} className="flex items-start gap-3 text-base leading-relaxed text-slate-700">
              <span className="mt-2.5 inline-block h-1.5 w-1.5 flex-shrink-0 rounded-full bg-blue-600" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );
    case "callout":
      return (
        <aside className="my-8 flex items-start gap-4 rounded-xl border-l-4 border-yellow-400 bg-yellow-50 p-5">
          <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-yellow-700" />
          <p className="text-sm leading-relaxed text-slate-800">{block.content}</p>
        </aside>
      );
    case "p":
    default:
      return (
        <p className="mt-5 text-base leading-relaxed text-slate-700">{block.content}</p>
      );
  }
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const post = BLOG_POSTS.find((p) => p.slug === slug);
  if (!post) return <Navigate to="/blog" replace />;

  const { service, suburb } = getPostMeta(post);
  const related = getRelatedPosts(slug, 3);
  const cover = post.coverImage || service?.image;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.excerpt,
    datePublished: post.dateISO,
    dateModified: post.dateISO,
    image: cover ? [cover] : undefined,
    author: {
      "@type": "Organization",
      name: BRAND.name,
      url: SITE_URL,
    },
    publisher: {
      "@type": "Organization",
      name: BRAND.name,
      logo: { "@type": "ImageObject", url: BRAND.logoUrl },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${SITE_URL}/blog/${post.slug}`,
    },
    about: [
      service && { "@type": "Service", name: service.title },
      suburb && { "@type": "Place", name: `${suburb.name}, NSW` },
    ].filter(Boolean),
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
      { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE_URL}/blog` },
      {
        "@type": "ListItem",
        position: 3,
        name: post.title,
        item: `${SITE_URL}/blog/${post.slug}`,
      },
    ],
  };

  return (
    <>
      <SEO
        title={post.title}
        description={post.excerpt}
        canonicalPath={`/blog/${post.slug}`}
        type="article"
        jsonLd={[articleSchema, breadcrumbSchema]}
      />

      {/* HERO */}
      <section
        className="relative isolate overflow-hidden bg-slate-900 text-white"
        data-testid="blog-post-hero"
      >
        {cover && (
          <>
            <img
              src={cover}
              alt=""
              aria-hidden
              className="absolute inset-0 -z-10 h-full w-full object-cover opacity-30"
            />
            <div className="absolute inset-0 -z-10 bg-gradient-to-b from-slate-900/95 via-slate-900/80 to-slate-900" />
          </>
        )}
        <div className="fg-container py-20 md:py-24">
          <Link
            to="/blog"
            data-testid="blog-back-link"
            className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-blue-300 hover:text-blue-200"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to blog
          </Link>

          <div className="mt-6 flex flex-wrap items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-blue-300">
            {suburb && (
              <Link
                to={`/service-areas/${suburb.slug}`}
                className="rounded-full bg-blue-600/20 px-3 py-1 text-blue-200 hover:bg-blue-600/40"
                data-testid="blog-tag-suburb"
              >
                {suburb.name}
              </Link>
            )}
            {service && (
              <Link
                to={`/services/${service.slug}`}
                className="rounded-full bg-yellow-400/20 px-3 py-1 text-yellow-200 hover:bg-yellow-400/40"
                data-testid="blog-tag-service"
              >
                {service.title}
              </Link>
            )}
          </div>

          <h1 className="mt-6 max-w-4xl font-display text-3xl font-extrabold leading-[1.1] tracking-tight sm:text-4xl lg:text-5xl">
            {post.title}
          </h1>

          <p className="mt-5 max-w-2xl text-base text-slate-300 sm:text-lg">
            {post.excerpt}
          </p>

          <div className="mt-7 flex flex-wrap items-center gap-5 text-sm text-slate-400">
            <span className="inline-flex items-center gap-2">
              <Calendar className="h-4 w-4" /> {formatDate(post.dateISO)}
            </span>
            <span className="inline-flex items-center gap-2">
              <Clock className="h-4 w-4" /> {post.readingMins} min read
            </span>
          </div>
        </div>
      </section>

      {/* BODY */}
      <section className="bg-white py-16 md:py-20">
        <div className="fg-container grid gap-12 md:grid-cols-12">
          <article className="md:col-span-8" data-testid="blog-post-body">
            {post.body.map((block, i) => (
              <Block key={i} block={block} />
            ))}

            {/* In-content CTA */}
            <div className="mt-12 rounded-2xl border border-blue-100 bg-blue-50 p-7">
              <div className="text-xs font-bold uppercase tracking-[0.18em] text-blue-700">
                Need a plumber {suburb ? `in ${suburb.name}` : "in Sydney"}?
              </div>
              <h3 className="mt-2 font-display text-2xl font-bold text-slate-900">
                Real plumbers, on the phone now.
              </h3>
              <p className="mt-2 text-sm text-slate-700">
                Free phone quote, fixed price on-site, 24/7 emergency response across Sydney.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <a
                  href={BRAND.phoneTel}
                  data-testid="blog-cta-call-btn"
                  className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-3 text-sm font-bold uppercase tracking-wider text-white hover:bg-blue-700"
                >
                  <Phone className="h-4 w-4" /> Call {BRAND.phone}
                </a>
                <Link
                  to="/contact#quote"
                  data-testid="blog-cta-quote-btn"
                  className="inline-flex items-center gap-2 rounded-lg border-2 border-slate-900 px-5 py-3 text-sm font-bold uppercase tracking-wider text-slate-900 hover:bg-slate-900 hover:text-white"
                >
                  Free Quote <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </article>

          {/* Sidebar */}
          <aside className="md:col-span-4">
            <div className="sticky top-24 space-y-6">
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="text-xs font-bold uppercase tracking-[0.18em] text-blue-700">
                  Free quote
                </div>
                <h3 className="mt-2 font-display text-xl font-bold text-slate-900">
                  Tell us about the job.
                </h3>
                <div className="mt-5">
                  <QuoteForm compact idPrefix={`blog-${slug}`} />
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="bg-slate-50 py-16 md:py-20" data-testid="blog-related-section">
          <div className="fg-container">
            <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              Related guides
            </h2>
            <div className="mt-8 grid gap-6 md:grid-cols-3">
              {related.map((r) => {
                const { service: rs, suburb: rsub } = getPostMeta(r);
                return (
                  <Link
                    key={r.slug}
                    to={`/blog/${r.slug}`}
                    data-testid={`blog-related-${r.slug}`}
                    className="fg-card-hover group rounded-2xl border border-slate-200 bg-white p-6"
                  >
                    <div className="flex flex-wrap items-center gap-2 text-[10px] font-bold uppercase tracking-[0.18em] text-blue-700">
                      {rsub && <span>{rsub.name}</span>}
                      {rs && rsub && <span className="text-slate-300">·</span>}
                      {rs && <span className="text-slate-500">{rs.title}</span>}
                    </div>
                    <div className="mt-3 font-display text-lg font-bold leading-snug text-slate-900 group-hover:text-blue-700">
                      {r.title}
                    </div>
                    <div className="mt-3 line-clamp-2 text-sm text-slate-600">
                      {r.excerpt}
                    </div>
                    <div className="mt-4 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-blue-700">
                      Read <ArrowRight className="h-3.5 w-3.5" />
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <CallStrip />
    </>
  );
}
